import { useEffect, useMemo, useRef, useState, type HTMLAttributes, type ReactNode } from 'react';
import { MDXProvider } from '@mdx-js/react';
import { Book, ContentCopy } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
  type AlertColor,
} from '@mui/material';
import { Navigate, useParams } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import DetailHeader from '../components/DetailHeader';
import { helpArticleMdxComponents } from '../components/help/helpArticleMdxComponents';
import {
  findHelpArticle,
  findHelpSection,
  getHelpSectionArticles,
  resolveHelpArticleLocale,
  slugifyHelpHeading,
  type HelpArticleDefinition,
} from '../help/articles';
import { buildCopyableHelpArticleSource } from '../help/helpArticleCopy';
import usePageTitle from '../hooks/usePageTitle';

function flattenHeadingText(children: ReactNode): string {
  if (typeof children === 'string' || typeof children === 'number') {
    return String(children);
  }
  if (!children) {
    return '';
  }
  if (Array.isArray(children)) {
    return children.map((child) => flattenHeadingText(child)).join('').trim();
  }
  if (typeof children === 'object' && 'props' in children) {
    return flattenHeadingText((children as { props?: { children?: ReactNode } }).props?.children);
  }
  return '';
}

function createHelpHeadingComponent(level: 2 | 3 | 4) {
  return function HelpHeading({
    children,
    id,
    ...props
  }: HTMLAttributes<HTMLHeadingElement> & { children?: ReactNode }) {
    const label = flattenHeadingText(children);
    const resolvedId = id || slugifyHelpHeading(label);
    const typographyVariant = level === 2 ? 'h5' : level === 3 ? 'h6' : 'subtitle1';

    return (
      <Box
        id={resolvedId}
        component={`h${level}`}
        {...props}
        sx={{
          scrollMarginTop: 112,
          mt: level === 2 ? 5 : 3.5,
          mb: level === 2 ? 1.5 : 1,
        }}
      >
        <Typography variant={typographyVariant} component="span">
          {children}
        </Typography>
      </Box>
    );
  };
}

function groupHelpArticlesByNavGroup(articles: HelpArticleDefinition[]) {
  const orderedGroups: Array<{ navGroupKey: string; articles: HelpArticleDefinition[] }> = [];

  articles.forEach((article) => {
    const existingGroup = orderedGroups.find((entry) => entry.navGroupKey === article.navGroupKey);
    if (existingGroup) {
      existingGroup.articles.push(article);
      return;
    }
    orderedGroups.push({
      navGroupKey: article.navGroupKey,
      articles: [article],
    });
  });

  return orderedGroups;
}

function resolveHelpSectionSlug(articlePath: string, articleSectionSlug?: string): string {
  if (articleSectionSlug) {
    return articleSectionSlug;
  }
  return articlePath.split('/')[0] || '';
}

function copyTextWithTextareaFallback(text: string): boolean {
  if (typeof document === 'undefined' || !document.body) {
    return false;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '0';
  textarea.style.left = '0';
  textarea.style.width = '1px';
  textarea.style.height = '1px';
  textarea.style.opacity = '0';
  textarea.style.pointerEvents = 'none';

  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();

  try {
    return document.execCommand('copy');
  } finally {
    document.body.removeChild(textarea);
  }
}

async function copyTextToClipboard(text: string): Promise<boolean> {
  if (!text.trim()) {
    return false;
  }

  if (copyTextWithTextareaFallback(text)) {
    return true;
  }

  if (!navigator.clipboard?.writeText) {
    return false;
  }

  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export default function HelpArticlePage() {
  const params = useParams<{ '*': string }>();
  const articlePath = params['*'] || '';
  const { t, i18n } = useTranslation(['pages', 'common']);
  const [copyFeedback, setCopyFeedback] = useState<{ message: string; severity: AlertColor } | null>(null);
  const [manualCopySource, setManualCopySource] = useState<string | null>(null);
  const manualCopyFieldRef = useRef<HTMLTextAreaElement | null>(null);

  const article = useMemo(() => findHelpArticle(articlePath), [articlePath]);
  const section = useMemo(() => {
    const sectionSlug = resolveHelpSectionSlug(articlePath, article?.sectionSlug);
    return findHelpSection(sectionSlug);
  }, [article, articlePath]);
  const sectionArticles = useMemo(
    () => (section ? getHelpSectionArticles(section.slug) : []),
    [section],
  );
  const groupedSectionArticles = useMemo(
    () => groupHelpArticlesByNavGroup(sectionArticles),
    [sectionArticles],
  );
  const resolvedArticleLocale = useMemo(() => {
    if (!article) {
      return null;
    }
    return resolveHelpArticleLocale(article, i18n.resolvedLanguage || i18n.language);
  }, [article, i18n.language, i18n.resolvedLanguage]);

  const articleTitle = article ? t(article.titleKey) : '';
  const sectionTitle = section ? t(section.titleKey) : t('pages:help.title');
  usePageTitle(articleTitle || sectionTitle);

  const entry = resolvedArticleLocale?.entry ?? null;
  const locale = resolvedArticleLocale?.locale ?? null;
  const fallbackUsed = resolvedArticleLocale?.fallbackUsed ?? false;
  const ArticleComponent = entry?.Component ?? null;
  const copyableArticleSource = useMemo(
    () => buildCopyableHelpArticleSource(entry?.source || '', {
      requestExamples: t('pages:help.apiReference.requestExamples'),
      responseExamples: t('pages:help.apiReference.responseExamples'),
      statusLabels: {
        200: t('pages:help.apiReference.statusLabels.ok'),
        401: t('pages:help.apiReference.statusLabels.unauthorized'),
        403: t('pages:help.apiReference.statusLabels.forbidden'),
        422: t('pages:help.apiReference.statusLabels.unprocessableEntity'),
        429: t('pages:help.apiReference.statusLabels.tooManyRequests'),
      },
      fallbackStatusLabel: t('pages:help.apiReference.statusLabels.generic'),
    }),
    [entry?.source, t],
  );
  const articleComponents = useMemo(
    () => ({
      ...helpArticleMdxComponents,
      h2: createHelpHeadingComponent(2),
      h3: createHelpHeadingComponent(3),
      h4: createHelpHeadingComponent(4),
    }),
    [],
  );

  useEffect(() => {
    if (!manualCopySource) {
      return undefined;
    }

    const selectTimer = window.setTimeout(() => {
      manualCopyFieldRef.current?.focus();
      manualCopyFieldRef.current?.select();
    }, 0);

    return () => window.clearTimeout(selectTimer);
  }, [manualCopySource]);

  if (!article && section) {
    return <Navigate to={`/help/${section.defaultArticleSlug}`} replace />;
  }

  if (!article || !section || !resolvedArticleLocale || !entry || !ArticleComponent || !locale) {
    return <Navigate to="/help" replace />;
  }

  const breadcrumbs =
    article.slug === section.defaultArticleSlug
      ? [
          { label: t('pages:help.title'), to: '/help' },
          { label: sectionTitle },
        ]
      : [
          { label: t('pages:help.title'), to: '/help' },
          { label: sectionTitle, to: `/help/${section.defaultArticleSlug}` },
          { label: articleTitle },
        ];

  const handleCopyPage = async () => {
    try {
      const source = copyableArticleSource;
      if (!source.trim()) {
        throw new Error(`Help page source is empty for ${article.slug} (${locale}).`);
      }

      const copied = await copyTextToClipboard(source);
      if (!copied) {
        console.warn('Help page clipboard access is unavailable; opening manual copy fallback.', {
          article: article.slug,
          locale,
        });
        setManualCopySource(source);
        setCopyFeedback({ message: t('pages:help.copyManualFallback'), severity: 'warning' });
        window.setTimeout(() => setCopyFeedback(null), 2500);
        return;
      }
      setCopyFeedback({ message: t('pages:help.copySuccess'), severity: 'success' });
      window.setTimeout(() => setCopyFeedback(null), 2500);
    } catch (copyError) {
      console.error('Error copying help page source:', copyError);
      setCopyFeedback({ message: t('pages:help.copyError'), severity: 'error' });
      window.setTimeout(() => setCopyFeedback(null), 2500);
    }
  };

  const handleRetryManualCopy = async () => {
    const source = manualCopySource || '';
    const copied = await copyTextToClipboard(source);
    if (copied) {
      setManualCopySource(null);
      setCopyFeedback({ message: t('pages:help.copySuccess'), severity: 'success' });
      window.setTimeout(() => setCopyFeedback(null), 2500);
      return;
    }

    manualCopyFieldRef.current?.focus();
    manualCopyFieldRef.current?.select();
    setCopyFeedback({ message: t('pages:help.copyManualFallback'), severity: 'warning' });
    window.setTimeout(() => setCopyFeedback(null), 2500);
  };

  return (
    <Stack spacing={3}>
      <DetailHeader
        title={articleTitle}
        subtitle={t(article.descriptionKey)}
        sectionIcon={<Book color="primary" />}
        breadcrumbs={breadcrumbs}
        actions={(
          <Button startIcon={<ContentCopy />} variant="outlined" onClick={() => void handleCopyPage()}>
            {t('pages:help.actions.copyPage')}
          </Button>
        )}
      />

      {fallbackUsed && (
        <Alert severity="info">
          {t('pages:help.fallbackNotice', { locale })}
        </Alert>
      )}
      {copyFeedback && <Alert severity={copyFeedback.severity}>{copyFeedback.message}</Alert>}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '300px minmax(0, 1fr)' },
          gap: { xs: 3, lg: 5 },
          alignItems: 'start',
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            position: { xs: 'static', lg: 'sticky' },
            top: 88,
            borderRadius: 2.5,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            p: 2,
          }}
        >
          <Stack spacing={2.5}>
            <Stack spacing={0.5}>
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.08em' }}>
                {t('pages:help.navigationLabel')}
              </Typography>
              <Typography variant="h6">{sectionTitle}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t(section.descriptionKey)}
              </Typography>
            </Stack>

            {groupedSectionArticles.map((group) => (
              <Stack key={group.navGroupKey} spacing={0.75}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                  {t(group.navGroupKey)}
                </Typography>
                <Stack spacing={0.25} sx={{ pl: 1 }}>
                  {group.articles.map((navArticle) => {
                    const isActive = navArticle.slug === article.slug;

                    return (
                      <Link
                        key={navArticle.slug}
                        component={RouterLink}
                        to={`/help/${navArticle.slug}`}
                        underline="none"
                        color={isActive ? 'primary.main' : 'text.secondary'}
                        aria-current={isActive ? 'page' : undefined}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          px: 1.25,
                          py: 0.95,
                          borderRadius: 1.5,
                          border: '1px solid',
                          borderColor: isActive ? 'rgba(255, 45, 109, 0.22)' : 'transparent',
                          fontWeight: isActive ? 700 : 500,
                          bgcolor: isActive ? 'rgba(255, 45, 109, 0.10)' : 'transparent',
                          transition: 'all 180ms ease',
                          '&:hover': {
                            color: 'primary.main',
                            bgcolor: isActive ? 'rgba(255, 45, 109, 0.14)' : 'action.hover',
                          },
                        }}
                      >
                        {navArticle.kind === 'endpoint' ? (
                          <>
                            <Chip
                              label={navArticle.method}
                              size="small"
                              color="success"
                              sx={{ fontWeight: 700, minWidth: 52 }}
                            />
                            <Typography variant="body2" component="span">
                              {t(navArticle.navLabelKey || navArticle.titleKey)}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2" component="span">
                            {t(navArticle.navLabelKey || navArticle.titleKey)}
                          </Typography>
                        )}
                      </Link>
                    );
                  })}
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Paper>

        <Box
          sx={{
            px: { xs: 0, md: 1 },
            '& h1': { typography: 'h4', mt: 0, mb: 2 },
            '& p': { typography: 'body1', lineHeight: 1.8, mb: 2 },
            '& ul, & ol': { pl: 3, mb: 2 },
            '& li': { mb: 0.75, lineHeight: 1.8 },
            '& :not(pre) > code, & p code, & li code, & td code': {
              fontFamily: 'monospace',
              fontSize: 13,
              px: 0.75,
              py: 0.25,
              borderRadius: 0.5,
              bgcolor: 'action.hover',
            },
            '& pre:not([data-help-code-block])': {
              p: 2,
              borderRadius: 1,
              overflowX: 'auto',
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              mb: 2,
            },
            '& pre:not([data-help-code-block]) code': {
              p: 0,
              bgcolor: 'transparent',
            },
            '& a': {
              color: 'primary.main',
            },
            '& blockquote': {
              m: 0,
              pl: 2,
              py: 0.5,
              borderLeft: '4px solid',
              borderColor: 'primary.main',
              color: 'text.secondary',
            },
            '& hr': {
              border: 0,
              borderTop: '1px solid',
              borderColor: 'divider',
              my: 3,
            },
          }}
        >
          <MDXProvider components={articleComponents}>
            <ArticleComponent components={articleComponents} />
          </MDXProvider>
        </Box>
      </Box>

      <Dialog
        open={Boolean(manualCopySource)}
        onClose={() => setManualCopySource(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{t('pages:help.manualCopy.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="warning">
              {t('pages:help.manualCopy.description')}
            </Alert>
            <TextField
              inputRef={manualCopyFieldRef}
              label={t('pages:help.manualCopy.fieldLabel')}
              value={manualCopySource || ''}
              multiline
              minRows={12}
              maxRows={24}
              fullWidth
              InputProps={{ readOnly: true }}
              onFocus={(event) => event.currentTarget.select()}
              sx={{
                '& textarea': {
                  fontFamily: 'monospace',
                  fontSize: 13,
                  lineHeight: 1.6,
                },
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualCopySource(null)}>
            {t('common:actions.close')}
          </Button>
          <Button
            variant="contained"
            startIcon={<ContentCopy />}
            onClick={() => void handleRetryManualCopy()}
          >
            {t('pages:help.actions.copyPage')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
